/* FLUSSO · CLONA SU PIÙ ACCOUNT
 *
 * Non è un elenco di conti con delle caselle. È la risposta a una domanda
 * sola: "quanti bot nascono, con quanto capitale ciascuno, e quanto impegno in
 * tutto" — su conti che muovono denaro vero.
 *
 * Le tre cose che questo flusso deve fare bene, in ordine:
 *  1. i conti inutilizzabili restano a schermo, SPENTI, col motivo accanto —
 *     chiave scaduta, sola lettura, saldo libero insufficiente con quanto
 *     manca, gas insufficiente per i bot su catena;
 *  2. una sola regola di ammissibilità per caselle, conteggio, riepilogo e
 *     creazione (vedi `bersagli.ts`);
 *  3. la creazione è N chiamate distinte: se la terza fallisce, le prime due
 *     sono nate davvero e va detto, non riassunto in "operazione fallita".
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Avvertenza,
  Distintivo,
  IconaSpunta,
  Modale,
  Pulsante,
  Occhiello,
  ContenitoreDati,
  DatoVivo,
  Campo,
  type StatoDatoVivo,
} from '../../componenti';
import { tradebots, eStantio, usaRichiesta } from '../../api';
import type { Tradebot } from '../../api';
import { num, ora } from '../../lib/formato';
import {
  ammissibili,
  caricaDatiBersagli,
  CAPITALE_MINIMO,
  costruisciBersagli,
  leggiCapitale,
  MAX_WALLET,
  motivoBlocco,
  type Bersaglio,
} from './bersagli';
import { corpoClone, coppiaBot, modalitaBot, sedeBot } from './modello';
import { DistintiviModalita, IconaClona, Interruttore, Motivo } from './Comuni';

interface DatiBersagli {
  /**
   * La ricetta COMPLETA del bot di origine, riletta apposta.
   *
   * La riga di lista arriva con `fields` ridotti e senza le credenziali
   * Telegram: clonare da quella creerebbe bot a cui manca metà della
   * configurazione — e che partono lo stesso, con denaro vero. Se la rilettura
   * fallisce non si clona: meglio non creare nulla che creare bot monchi.
   */
  sorgente: Tradebot | null;
  bersagli: Bersaglio[];
  /** `true` quando i saldi non si sono potuti leggere del tutto. */
  saldiMuti: boolean;
  troncato: number;
}

interface EsitoCreazione {
  nome: string;
  riuscito: boolean;
  messaggio: string;
}

export function FoglioClona({
  bot,
  aperta,
  onChiudi,
  onCreati,
}: {
  bot: Tradebot | null;
  aperta: boolean;
  onChiudi: () => void;
  /** Chiamata quando almeno un bot è nato: la lista va riletta. */
  onCreati: (quanti: number) => void;
}) {
  const [capitaleGrezzo, setCapitaleGrezzo] = useState('');
  const [scelti, setScelti] = useState<number[]>([]);
  const [avviaSubito, setAvviaSubito] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [esiti, setEsiti] = useState<EsitoCreazione[] | null>(null);

  const idBot = bot?.id ?? 0;
  const suDex = bot?.walletId != null;

  /* Riaprire il foglio su un altro bot non deve ereditare la scelta fatta sul
     precedente: si creerebbero bot su conti scelti per un'altra strategia. */
  useEffect(() => {
    if (!aperta) return;
    setScelti([]);
    setEsiti(null);
    setAvviaSubito(false);
    setCapitaleGrezzo(bot?.currencyBalance != null ? String(bot.currencyBalance) : '');
  }, [aperta, idBot, bot?.currencyBalance]);

  const richiesta = usaRichiesta<DatiBersagli>(
    async (segnale) => {
      if (!bot) return { sorgente: null, bersagli: [], saldiMuti: false, troncato: 0 };

      const sorgente = await tradebots.uno(bot.id).catch(() => null);
      /* Letture e motivi di blocco vengono dalle stesse funzioni che usa la
         creazione: stesso conto, stesso verdetto, nei due flussi. Qui la
         configurazione di riferimento e' il bot di origine, che esclude anche
         la sede su cui gira gia'. */
      const dati = await caricaDatiBersagli(bot, segnale);

      return {
        sorgente,
        bersagli: costruisciBersagli(dati, bot),
        saldiMuti: dati.saldiMuti,
        troncato: dati.troncato,
      };
    },
    [idBot, aperta],
    { attiva: aperta && bot != null, eVuoto: (d) => d.bersagli.length === 0 },
  );

  const bersagli = richiesta.dati?.bersagli ?? [];
  const capitale = leggiCapitale(capitaleGrezzo);
  const stantio = eStantio(richiesta.letturaA);

  /* Cambiando il capitale la selezione si pota da sola: lasciarla appesa su
     conti che non la reggono più darebbe un conteggio che promette bot che non
     nasceranno. */
  useEffect(() => {
    setScelti((precedenti) => {
      const validi = ammissibili(bersagli, precedenti, capitale.valore).map((b) => b.id);
      return validi.length === precedenti.length ? precedenti : validi;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capitaleGrezzo, richiesta.letturaA]);

  const eleggibili = useMemo(
    () => ammissibili(bersagli, scelti, capitale.valore),
    [bersagli, scelti, capitale.valore],
  );

  const quanti = eleggibili.length;
  const impegnoTotale = quanti * capitale.valore;

  const statoVivo: StatoDatoVivo = richiesta.stato === 'caricamento'
    ? 'caricamento'
    : richiesta.dati?.saldiMuti || richiesta.stato === 'errore'
      ? 'muto'
      : stantio
        ? 'stantio'
        : bersagli.length > 0 && bersagli.every((b) => b.bloccoFisso?.startsWith('gas insufficiente'))
          ? 'insufficiente'
          : 'letto';

  const sorgente = richiesta.dati?.sorgente ?? null;

  const motivoPulsante = !capitale.valido
    ? capitale.motivo
    : quanti === 0
      ? 'Scegli almeno un conto utilizzabile.'
      : richiesta.stato === 'pieno' && sorgente == null
        ? 'La configurazione completa del bot di origine non è stata riletta: non si clona una ricetta parziale.'
        : null;

  async function crea() {
    if (!bot || !sorgente || motivoPulsante || inCorso) return;
    setInCorso(true);

    /* Una POST per bersaglio, in sequenza: il backend non ha una creazione
       multipla transazionale, quindi un fallimento a metà lascia dei bot già
       creati. Vanno elencati uno per uno, altrimenti si riprova tutto e
       nascono doppioni che operano con denaro vero. */
    const risultati: EsitoCreazione[] = [];
    for (const b of eleggibili) {
      try {
        await tradebots.crea(
          corpoClone(sorgente, { tipo: b.tipo, id: b.id }, capitale.valore, avviaSubito),
        );
        risultati.push({
          nome: b.nome,
          riuscito: true,
          messaggio: avviaSubito ? 'creato e avviato' : 'creato, fermo',
        });
      } catch (e) {
        risultati.push({
          nome: b.nome,
          riuscito: false,
          messaggio: e instanceof Error ? e.message : 'creazione non riuscita',
        });
      }
    }

    setInCorso(false);
    setEsiti(risultati);
    const nati = risultati.filter((r) => r.riuscito).length;
    if (nati > 0) onCreati(nati);
  }

  if (!bot) return null;

  const modalita = modalitaBot(bot);

  return (
    <Modale
      aperta={aperta}
      onChiudi={onChiudi}
      titolo="Clona su altri conti"
      sottotitolo="stessa ricetta, un bot per conto — saldi e storia ripartono da zero"
      larga
      pieda={
        esiti ? (
          <Pulsante variante="primario" largo onClick={onChiudi}>
            Chiudi
          </Pulsante>
        ) : (
          <>
            <Pulsante onClick={onChiudi} disabled={inCorso}>
              Annulla
            </Pulsante>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Pulsante variante="primario" largo onClick={crea} disabled={!!motivoPulsante || inCorso}>
                <IconaClona />
                {inCorso
                  ? 'Creazione in corso…'
                  : quanti === 0
                    ? 'Nessun bot da creare'
                    : quanti === 1
                      ? 'Crea 1 bot'
                      : `Crea ${quanti} bot`}
              </Pulsante>
              {/* Il motivo sta accanto al pulsante: in cima al foglio, chi prova
                  a premere non saprebbe perché non succede nulla. */}
              {motivoPulsante && <span className="sb-motivo-blocco">{motivoPulsante}</span>}
            </div>
          </>
        )
      }
    >
      {esiti ? (
        <Esiti esiti={esiti} />
      ) : (
        <>
          <div className="sb-scheda sb-scheda--incassata">
            <div className="tb-testata">
              <div className="tb-testata__corpo">
                <div className="tb-testata__titolo">
                  <span className="tb-coppia">{coppiaBot(bot)}</span>
                  <DistintiviModalita bot={bot} />
                </div>
                <div className="tb-sotto">
                  {bot.strategyName} · {sedeBot(bot)} · #{bot.id}
                </div>
              </div>
            </div>
          </div>

          {modalita.denaroReale && (
            <Avvertenza livello="grave">
              Il bot di origine opera con <b>denaro vero</b>: i cloni nascono con la stessa
              impostazione e comprano davvero. {avviaSubito ? 'Con “avvia subito” partono appena creati.' : 'Restano fermi finché non li avvii.'}
            </Avvertenza>
          )}

          {/* ---------- capitale, col saldo letto accanto ---------- */}
          <div>
            <Occhiello>Capitale per ogni bot</Occhiello>
            <div className="sb-riga" style={{ alignItems: 'flex-start', marginTop: 'var(--sb-s-4)' }}>
              <div style={{ flex: '1 1 170px', minWidth: 150 }}>
                <Campo
                  etichetta={`Importo in ${bot.currency}`}
                  suggerimento={`minimo ${num(CAPITALE_MINIMO)}`}
                  type="number"
                  min={CAPITALE_MINIMO}
                  step="any"
                  inputMode="decimal"
                  value={capitaleGrezzo}
                  onChange={(e) => setCapitaleGrezzo(e.target.value)}
                  unita={bot.currency}
                  errore={capitaleGrezzo !== '' && !capitale.valido ? capitale.motivo : null}
                />
              </div>
              <DatoVivo
                valore={
                  richiesta.stato === 'caricamento'
                    ? 'lettura…'
                    : `${bersagli.filter((b) => b.saldoLibero != null).length}/${bersagli.length} letti`
                }
                meta={
                  richiesta.letturaA
                    ? `saldi liberi in ${bot.currency} · letti alle ${ora(richiesta.letturaA)}`
                    : `saldi liberi in ${bot.currency}`
                }
                stato={statoVivo}
              />
            </div>
            {/* La differenza fra i due percorsi non è un dettaglio tecnico: cambia cosa
                succede ai soldi, e va letta prima di clonare. */}
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
              Su un <b>conto di exchange</b> è un tetto: il bot non impegna più di questa cifra, e
              più bot possono lavorare sullo stesso conto.
            </div>
            <Avvertenza livello="cautela">
              Su un <b>wallet</b> non è un tetto. Un bot su catena impegna tutto il saldo
              dell’indirizzo: per limitarne l’esposizione si usa un wallet dedicato, non questa
              cifra. Qui serve solo a dichiarare con quanto nasce.
            </Avvertenza>
          </div>

          <Interruttore
            etichetta={avviaSubito ? 'Avvia subito' : 'Crea fermo'}
            acceso={avviaSubito}
            onCambia={() => setAvviaSubito((v) => !v)}
          />

          {stantio && richiesta.letturaA && (
            <Avvertenza livello="cautela">
              I saldi sono di prima, letti alle {ora(richiesta.letturaA)}. Rileggili prima di
              impegnare capitale.{' '}
              <button
                type="button"
                onClick={richiesta.ricarica}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  color: 'var(--sb-accento-chiaro)',
                  textDecoration: 'underline',
                  minHeight: 'var(--sb-tocco-min)',
                }}
              >
                Rileggi ora
              </button>
            </Avvertenza>
          )}

          {/* ---------- i bersagli ---------- */}
          <div>
            <Occhiello>
              {suDex ? 'Wallet di destinazione' : 'Conti di destinazione'}
            </Occhiello>
            <div style={{ marginTop: 'var(--sb-s-4)' }}>
              <ContenitoreDati
                /* Durante una rilettura non si mostra l'elenco vecchio: se il
                   foglio si riapre su un altro bot, le ragioni del blocco sono
                   quelle dell'altra valuta e dell'altra catena. */
                stato={richiesta.inAggiornamento ? 'caricamento' : richiesta.stato}
                causaVuoto={
                  suDex
                    ? 'Non ci sono altri wallet oltre a quello su cui gira già questo bot.'
                    : 'Non ci sono altri conti oltre a quello su cui gira già questo bot.'
                }
                messaggioErrore={richiesta.errore?.message}
                onRiprova={richiesta.ricarica}
              >
                <div className="tb-bersagli">
                  {bersagli.map((b) => (
                    <RigaBersaglio
                      key={`${b.tipo}-${b.id}`}
                      bersaglio={b}
                      capitale={capitale}
                      scelto={scelti.includes(b.id)}
                      onCambia={() =>
                        setScelti((s) =>
                          s.includes(b.id) ? s.filter((x) => x !== b.id) : [...s, b.id],
                        )
                      }
                    />
                  ))}
                </div>
              </ContenitoreDati>
            </div>

            {richiesta.dati && richiesta.dati.troncato > 0 && (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                Altri {richiesta.dati.troncato} wallet non sono in elenco: il riepilogo dei saldi ne
                accetta al massimo {MAX_WALLET} per richiesta.
              </div>
            )}
          </div>

          {/* ---------- il riepilogo ---------- */}
          <div className="sb-scheda sb-scheda--incassata">
            <Occhiello>Riepilogo</Occhiello>
            <div className="tb-riepilogo" style={{ marginTop: 'var(--sb-s-4)' }}>
              {!capitale.valido ? (
                capitale.motivo
              ) : quanti === 0 ? (
                'Nessun conto scelto fra quelli utilizzabili.'
              ) : (
                <>
                  Nascono <b>{quanti === 1 ? '1 bot' : `${quanti} bot`}</b> da{' '}
                  <b>
                    {num(capitale.valore, 2)} {bot.currency}
                  </b>{' '}
                  ciascuno, per <b>{num(impegnoTotale, 2)} {bot.currency}</b> impegnati in tutto.{' '}
                  {avviaSubito
                    ? 'Partono appena creati e restano in warm-up finché non hanno abbastanza candele.'
                    : 'Restano fermi finché non li avvii tu, uno per uno.'}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Modale>
  );
}

/* ==========================================================================
 * Una riga di bersaglio
 * ======================================================================== */

/**
 * Una riga di bersaglio, condivisa fra clonazione e creazione.
 *
 * Il motivo del blocco viene sempre da `motivoBlocco`: se questa riga si
 * spegnesse per conto suo, un conto potrebbe risultare scelto nel conteggio e
 * spento a schermo, che e' il difetto da cui `bersagli.ts` e' nato. `singola`
 * cambia solo il ruolo dichiarato: nella clonazione si segnano piu' conti,
 * nella creazione se ne sceglie uno.
 */
export function RigaBersaglio({
  bersaglio: b,
  capitale,
  scelto,
  singola = false,
  onCambia,
}: {
  bersaglio: Bersaglio;
  capitale: { valido: boolean; valore: number };
  scelto: boolean;
  singola?: boolean;
  onCambia: () => void;
}) {
  const blocco = motivoBlocco(b, capitale.valore);
  /* Senza un capitale valido non si scrive "saldo insufficiente" su ogni riga:
     l'errore sta accanto al campo, e le righe restano inerti. */
  const inerte = !capitale.valido || blocco != null;
  const segnata = scelto && !inerte;

  const classi = [
    'tb-bersaglio',
    segnata ? 'tb-bersaglio--scelto' : '',
    inerte ? 'tb-bersaglio--spento' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classi}
      onClick={inerte ? undefined : onCambia}
      disabled={inerte}
      role={singola ? 'radio' : undefined}
      aria-checked={singola ? segnata : undefined}
      aria-pressed={singola ? undefined : segnata}
    >
      <span
        className={`tb-casella${segnata ? ' tb-casella--segnata' : ''}${inerte ? ' tb-casella--spenta' : ''}`}
        aria-hidden="true"
      >
        <IconaSpunta />
      </span>
      <span className="tb-bersaglio__corpo">
        <span className="tb-bersaglio__nome">
          {b.nome}
          <span className="tb-bersaglio__saldo">
            {b.saldoLibero == null
              ? 'saldo non letto'
              : `${num(b.saldoLibero, 2)} ${b.valuta} liberi`}
          </span>
        </span>
        <span className="tb-bersaglio__genere">{b.genere}</span>
        {blocco && <Motivo testo={blocco} grave={b.bloccoGrave} />}
        {!blocco && !capitale.valido && (
          <span className="tb-bersaglio__genere">
            in attesa del capitale: senza importo non si può dire se il saldo basta
          </span>
        )}
      </span>
    </button>
  );
}

/* ==========================================================================
 * Esiti, uno per bersaglio
 * ======================================================================== */

function Esiti({ esiti }: { esiti: EsitoCreazione[] }) {
  const nati = esiti.filter((e) => e.riuscito).length;
  const falliti = esiti.length - nati;

  return (
    <>
      {falliti > 0 ? (
        <Avvertenza livello="grave">
          {nati === 0
            ? 'Nessun bot è nato.'
            : `${nati} ${nati === 1 ? 'bot è nato' : 'bot sono nati'}, ${falliti} no.`}{' '}
          Ripetere la clonazione ricreerebbe anche quelli riusciti: controlla la lista prima di
          riprovare.
        </Avvertenza>
      ) : (
        <Avvertenza livello="nota">
          {nati === 1 ? '1 bot creato.' : `${nati} bot creati.`} Li trovi in cima alla lista.
        </Avvertenza>
      )}

      <div>
        {esiti.map((e) => (
          <div className="tb-esito" key={e.nome}>
            <Distintivo tono={e.riuscito ? 'guadagno' : 'perdita'}>
              {e.riuscito ? 'creato' : 'fallito'}
            </Distintivo>
            <span className="tb-esito__nome">
              {e.nome}
              <div className="sb-nota">{e.messaggio}</div>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
